import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { VideoPlayer, ChatWidget } from '../components';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const HERO_VIDEOS = [
  'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
  'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/friday.mp4',
  'https://download.blender.org/peach/bigbuckbunny_movies/BigBuckBunny_320x180.mp4',
];
const HERO_VIDEO_SRC = HERO_VIDEOS[Math.floor(Math.random() * HERO_VIDEOS.length)];

const Homepage = () => {
  const aboutAnim = useScrollAnimation();
  const servicesAnim = useScrollAnimation();
  const launchAnim = useScrollAnimation();

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen w-full flex items-center bg-bg overflow-hidden">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-28 grid grid-cols-1 lg:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)] gap-10 lg:gap-14 items-center">
          {/* Video player — replaces the old bubble */}
          <div className="order-2 lg:order-1 w-full max-w-[640px] justify-self-center lg:justify-self-start">
            <div className="relative aspect-video w-full rounded-card overflow-hidden shadow-2xl shadow-chart/10 ring-1 ring-line">
              <VideoPlayer
                src={HERO_VIDEO_SRC}
                autoplay={false}
                controls
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <h1 className="font-display text-[3.5rem] md:text-[5.5rem] lg:text-[6.5rem] leading-none text-text uppercase mb-8 tracking-wide">
              <span className="block">FOR THE</span>
              <span className="block text-chart">FOUNDERS</span>
              <span className="block">OF TOMORROW</span>
            </h1>

            <p className="font-serif-display text-2xl md:text-3xl italic text-muted leading-relaxed max-w-lg mb-10">
              Virtual incubation for Pakistan's next generation of founders. Structured programs, group mentorship, and investor access — all in one platform.
            </p>

            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="bg-chart text-black px-8 py-3 rounded-full font-medium hover:bg-chart/90 transition-all duration-200 text-lg shadow-lg"
              >
                Login
              </Link>
              <div className="w-8 h-px bg-line"></div>
              <div className="w-12 h-12 bg-surface border border-line rounded-full flex items-center justify-center hover:bg-accent-soft transition-colors cursor-pointer">
                <svg className="w-5 h-5 text-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-bg">
        <div ref={aboutAnim.ref} className={`animate-on-scroll ${aboutAnim.isVisible ? 'visible' : ''}`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - About Text */}
              <div className="space-y-6">
                <h2 className="font-display text-[4.5rem] md:text-[5.5rem] leading-none text-text uppercase tracking-wide">
                  About
                </h2>

                <div className="space-y-4 text-muted font-serif-display text-2xl italic leading-relaxed">
                  <p>
                    BunyadKar is a virtual incubation center designed to equip early-stage entrepreneurs with the skills, mentorship, and resources they need to launch and grow successful startups.
                  </p>
                  <p>
                    Through a structured 6-phase virtual incubation program, group mentorship from seasoned industry experts, and a curated library of tools and templates, we give founders the confidence and clarity to take their ideas from concept to market.
                  </p>
                </div>

                <Link to="/about" className="flex items-center space-x-2 text-text hover:text-muted transition-colors">
                  <span className="font-serif-display text-xl italic">Learn more</span>
                  <div className="w-1 h-1 bg-text rounded-full"></div>
                </Link>
              </div>

              {/* Right Column - Statistics with Overlay Bubble */}
              <div className="relative">
                {/* Statistics overlaid on bubble */}
                <div className="relative z-10 grid grid-cols-3 gap-8 mb-8">
                  <div className="text-center">
                    <div className="font-display text-5xl md:text-6xl text-chart leading-none mb-2">100+</div>
                    <div className="text-sm text-muted font-sans">Startups Supported</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display text-5xl md:text-6xl text-chart leading-none mb-2">32</div>
                    <div className="text-sm text-muted font-sans">Expert Mentors</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display text-5xl md:text-6xl text-chart leading-none mb-2">03</div>
                    <div className="text-sm text-muted font-sans">Programs Offered</div>
                  </div>
                </div>

                {/* Real Bubble Images */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 z-0">
                  <img
                    src="/homepage-graphics/bubble-1.png"
                    alt="Decorative bubble"
                    className="w-80 h-80 object-contain opacity-90"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-bg relative">
        {/* Bubble positioned from the very edge of the page */}
        <div className="absolute bottom-0 right-0 transform translate-x-[50px] -translate-y-16 z-0">
          <img
            src="/homepage-graphics/bubble-2.png"
            alt="Decorative bubble"
            className="w-[500px] h-[300px] object-contain opacity-60"
          />
        </div>

        <div ref={servicesAnim.ref} className={`animate-on-scroll ${servicesAnim.isVisible ? 'visible' : ''}`}>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            {/* Top Content */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-16">
              <div className="mb-8 lg:mb-0">
                <h2 className="font-display text-[4.5rem] md:text-[5.5rem] leading-none text-text uppercase tracking-wide mb-6">
                  Our Services
                </h2>
                <p className="font-serif-display text-2xl italic text-muted leading-relaxed max-w-2xl">
                  Everything you need to validate your idea, build your product, and scale your startup — from structured courses and expert mentorship to progress tracking and curated resources.
                </p>
              </div>

              <div className="flex items-center">
                <Link
                  to="/login"
                  className="bg-chart text-black px-6 py-2 rounded-full font-medium hover:bg-chart/90 transition-all duration-200 shadow-lg"
                >
                  View Programs
                </Link>
                <div className="w-6 h-px bg-gray-300 mx-1"></div>
                <div className="w-10 h-10 bg-shell rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Service Cards Grid */}
            <div className="relative max-w-6xl">
              {/* Row 1: Structured Learning + 1:1 Mentorship (long) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 relative z-10">
                {/* Structured Learning */}
                <Link
                  to="/login"
                  className="block p-8 rounded-2xl border bg-transparent text-text border-line hover:border-black/15 transition-all duration-300 hover:bg-black/5"
                >
                  <div className="w-12 h-12 mb-6">
                    <img
                      src="/homepage-graphics/structured-dashboard-icon.png"
                      alt="Structured Learning"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="font-display text-3xl tracking-wide text-text mb-4">Structured Learning</h3>
                  <p className="font-serif-display text-xl italic leading-relaxed text-muted">
                    Access expert-led courses covering business fundamentals, product development, and growth strategy.
                  </p>
                </Link>

                {/* 1:1 Mentorship - Long box spanning 2 columns */}
                <Link
                  to="/login"
                  className="block md:col-span-2 p-8 rounded-2xl border bg-transparent text-text border-line hover:border-black/15 transition-all duration-300 hover:bg-black/5"
                >
                  <div className="w-12 h-12 mb-6">
                    <img
                      src="/homepage-graphics/one-on-one-management-icon.png"
                      alt="1:1 Mentorship"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="font-display text-3xl tracking-wide text-text mb-4">Group Mentorship</h3>
                  <p className="font-serif-display text-xl italic leading-relaxed text-muted">
                    Join structured group sessions led by experienced mentors who have built and scaled companies. Get actionable feedback alongside your peers.
                  </p>
                </Link>
              </div>

              {/* Row 2: Progress Dashboard + Resources Hub + Startup Hub */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
                {/* Progress Dashboard */}
                <Link
                  to="/login"
                  className="block p-8 rounded-2xl border bg-chart text-white border-chart transition-all duration-300 hover:bg-chart/90"
                >
                  <div className="w-12 h-12 mb-6">
                    <img
                      src="/homepage-graphics/progress-dashboard-icon.png"
                      alt="Progress Dashboard"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="font-display text-3xl tracking-wide text-black mb-4">Progress Dashboard</h3>
                  <p className="font-serif-display text-lg italic leading-relaxed text-black/80">
                    Track your milestones, monitor your learning progress, and stay on top of key metrics from one intuitive dashboard.
                  </p>
                </Link>

                {/* Resources Hub */}
                <Link
                  to="/login"
                  className="block p-8 rounded-2xl border bg-transparent text-text border-line hover:border-black/15 transition-all duration-300 hover:bg-black/5"
                >
                  <div className="w-12 h-12 mb-6">
                    <img
                      src="/homepage-graphics/resources-icon.png"
                      alt="Resources Hub"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="font-display text-3xl tracking-wide text-text mb-4">Resources Hub</h3>
                  <p className="font-serif-display text-xl italic leading-relaxed text-muted">
                    Browse templates, guides, pitch decks, and tools curated by industry experts.
                  </p>
                </Link>

                {/* Startup Hub */}
                <Link
                  to="/login"
                  className="block p-8 rounded-2xl border bg-transparent border-line hover:border-black/15 transition-all duration-300 hover:bg-black/5 relative"
                >
                  <div className="w-12 h-12 mb-6">
                    <svg className="w-full h-full text-text" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M2.81 14.126l5.482 5.482c.39.39 1.023.39 1.414 0l9.126-9.126c.39-.39.39-1.023 0-1.414l-5.482-5.482c-.39-.39-1.023-.39-1.414 0L2.81 12.712c-.39.39-.39 1.023 0 1.414z"/>
                    </svg>
                  </div>
                  <h3 className="font-display text-3xl tracking-wide text-text mb-4">Startup Hub</h3>
                  <p className="font-serif-display text-xl italic leading-relaxed text-muted">
                    Build and launch your startup with our comprehensive tools and resources.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Launch Section */}
      <section className="py-24 bg-bg relative">
        {/* Real Bubble Shape at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden">
          <img
            src="/homepage-graphics/bubble-3.png"
            alt="Decorative bubble"
            className="w-full h-32 object-fill transform translate-y-0 opacity-100"
          />
        </div>

        <div ref={launchAnim.ref} className={`animate-on-scroll ${launchAnim.isVisible ? 'visible' : ''}`}>
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <div className="text-left">
              <h2 className="font-display text-[4.5rem] md:text-[5.5rem] leading-none text-text uppercase tracking-wide mb-8">
                Ready to Launch?
              </h2>

              <p className="font-serif-display text-2xl md:text-3xl italic text-muted leading-relaxed mb-12 max-w-3xl">
                Every big business begins with a small idea — and the right support to grow. Our platform connects you with mentors, resources, and tools that help you move forward with confidence.
              </p>
            </div>

            <div className="flex justify-end">
              <div className="flex items-center">
                <Link
                  to="/login"
                  className="bg-chart text-black px-8 py-3 rounded-full font-medium hover:bg-chart/90 transition-all duration-200 text-lg shadow-lg"
                >
                  Get Started
                </Link>
                <div className="w-8 h-px bg-gray-300 mx-2"></div>
                <div className="w-12 h-12 bg-shell rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default Homepage;
